import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, CheckCircle2, CreditCard, Calendar, ArrowUpCircle, XCircle, InfoIcon, Check, Star, Sparkles, Gem, Award } from 'lucide-react';
import PricingTable from '@/components/PricingTable';

interface SubscriptionDetails {
  id: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  productName: string;
  productId: string;
}

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  image: string | null;
  defaultPrice: {
    id: string;
    currency: string;
    unit_amount: number;
    recurring: {
      interval: string;
      interval_count: number;
    } | null;
    trial_period_days: number | null;
  } | null;
  features: Array<{ name: string }>;
  metadata: Record<string, string>;
  popular?: boolean;
}

const MySubscription = () => {
  const { user, getToken, email } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [availablePlans, setAvailablePlans] = useState<PricingPlan[]>([]);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);

  // Format currency for display
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };
  
  // Format date for display
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleViewBillingHistory = async () => {
    try {
      setOpeningBillingPortal(true);
      const response = await fetch('/api/create-billing-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/my-subscription`,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error creating billing portal session: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Billing Portal
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No billing portal URL returned from API');
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      toast({
        title: "Error",
        description: "Could not open billing portal. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setOpeningBillingPortal(false);
    }
  };

  // Get current subscription info
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const token = await getToken();
        // Send Clerk email in body
        console.log('[MySubscription] Checking subscription for email:', email);
        const response = await fetch(`/api/check-subscription?email=${encodeURIComponent(email || '')}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`Error checking subscription: ${response.status}`);
        }
        const data = await response.json();
        if (!data.hasActiveSubscription) {
          setSubscription(null);
          return;
        }
        setSubscription(data.subscription);
      } catch (err) {
        console.error('Error fetching subscription status:', err);
        toast({
          title: "Error",
          description: "Could not load your subscription details. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  // Only run when user changes, not on every render (exclude getToken/toast stability)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch available plans for upgrades/downgrades
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await fetch('/api/get-pricing-plans');
        
        if (!response.ok) {
          throw new Error(`Error fetching plans: ${response.status}`);
        }
        
        const data = await response.json();
        setAvailablePlans(data.plans || []);
      } catch (err) {
        console.error('Failed to fetch pricing plans:', err);
        toast({
          title: "Error",
          description: "Could not load available plans. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoadingPlans(false);
      }
    };
    
    if (subscription) {
      fetchPricingPlans();
    }
  // Only refetch plans when subscription initial value changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const handleCancelSubscription = async (immediate = false) => {
    if (!subscription) return;
    
    try {
      setCancellingSubscription(true);
      const token = await getToken();
      // Include Clerk email so server can authenticate and lookup customer
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          email,
          subscriptionId: subscription.id,
          cancelAtPeriodEnd: !immediate // true to cancel at period end, false for immediate
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error cancelling subscription: ${response.status}`);
      }
      
      const data = await response.json();
      // Update subscription state with returned values and preserve product info
      setSubscription({
        id: data.subscription.id,
        status: data.subscription.status,
        currentPeriodEnd: data.subscription.currentPeriodEnd,
        cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd,
        productName: subscription.productName,
        productId: subscription.productId
      });
      // Show confirmation toast
      toast({
        title: "Subscription Cancelled",
        description: data.message,
        variant: "default",
      });
      // Refresh subscription state
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      toast({
        title: "Action Failed",
        description: "Could not process your cancellation request. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleChangePlan = async () => {
    if (!subscription || !selectedPlanId) return;
    
    const selectedPlan = availablePlans.find(plan => 
      plan.id === selectedPlanId
    );
    
    if (!selectedPlan || !selectedPlan.defaultPrice) {
      toast({
        title: "Invalid Selection",
        description: "Please select a valid plan to upgrade or downgrade to.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setUpdatingSubscription(true);
      const token = await getToken();
      const response = await fetch('/api/update-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          subscriptionId: subscription.id,
          newPriceId: selectedPlan.defaultPrice.id
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error updating subscription: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update local state with new subscription
      setSubscription(data.subscription);
      
      toast({
        title: "Subscription Updated",
        description: `Your subscription has been changed to ${data.subscription.productName}.`,
        variant: "default",
      });
      
      // Clear selection
      setSelectedPlanId(null);
    } catch (err) {
      console.error('Error updating subscription:', err);
      toast({
        title: "Action Failed",
        description: "Could not update your subscription. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setUpdatingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-bold mb-8">My Subscription</h1>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-lg text-gray-600">Loading subscription details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle not authenticated case
  if (!user) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-3xl font-bold mb-4 text-center">Authentication Required</h1>
          <p className="text-lg text-gray-600 mb-8 text-center">
            Please sign in to view and manage your subscription details.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 mb-10 text-center">
            <div className="flex flex-col items-center">
              <AlertCircle className="h-16 w-16 text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-blue-800 mb-3">Sign In Required</h2>
              <p className="text-blue-700 mb-6 max-w-lg mx-auto">
                You need to be signed in to manage your subscriptions. Sign in to access your account and subscription details.
              </p>
              <Button 
                onClick={() => navigate('/sign-in')} 
                className="bg-blue-600 hover:bg-blue-700 px-6"
              >
                Sign In
              </Button>
              <p className="text-sm text-blue-600 mt-4">
                Don't have an account? Sign in with Google to create one automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    // Only render PricingTable in standalone mode (no subscription status props)
    return (
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold mb-4 text-center">Select a Subscription Plan</h1>
          <PricingTable />

          <div className="mt-8 flex justify-center">
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2 text-center">My Subscription</h1>
        <p className="text-lg text-gray-600 mb-10 text-center">
          Manage your current subscription and billing details.
        </p>
        {/* Current Subscription Status Card */}
        <Card className="mb-10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{subscription.productName}</CardTitle>
                <CardDescription className="mt-2">
                  <div className="text-sm flex items-center">
                    <Calendar className="w-4 h-4 mr-1 inline" /> 
                    Current billing period ends: {formatDate(subscription.currentPeriodEnd)}
                  </div>
                </CardDescription>
              </div>
              <div>
                <Badge variant={subscription.cancelAtPeriodEnd ? "destructive" : "default"} className="text-xs">
                  {subscription.cancelAtPeriodEnd 
                    ? "Cancels at period end" 
                    : subscription.status === "active" ? "Active" : subscription.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start">
                {subscription.cancelAtPeriodEnd ? (
                  <div className="flex items-center p-4 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200 w-full">
                    <InfoIcon className="mr-3 h-5 w-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">Your subscription will end on {formatDate(subscription.currentPeriodEnd)}</p>
                      <p className="text-sm mt-1">Reactivate anytime before then to maintain access.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center p-4 bg-green-50 text-green-700 rounded-lg border border-green-200 w-full">
                    <CheckCircle2 className="mr-3 h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">Your subscription is active</p>
                      <p className="text-sm mt-1">Full access to {subscription.productName} features.</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Cancellation actions */}
              <div className="flex space-x-4">
                <Button
                  variant="destructive"
                  disabled={cancellingSubscription}
                  onClick={() => window.confirm('Cancel at period end?') && handleCancelSubscription(true)}
                >
                  {cancellingSubscription ? 'Cancelling...' : 'Cancel at Period End'}
                </Button>
                <Button
                  variant="destructive"
                  disabled={cancellingSubscription}
                  onClick={() => window.confirm('Cancel now?') && handleCancelSubscription(false)}
                >
                  {cancellingSubscription ? 'Cancelling...' : 'Cancel Now'}
                </Button>
              </div>
              {/* Pricing management */}
              <div className="pt-8">
                <PricingTable
                  hasActiveSubscription={!!subscription}
                  subscriptionDetails={subscription}
                  setHasActiveSubscription={val => !val && setSubscription(null)}
                  setSubscriptionDetails={setSubscription}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col items-start border-t border-gray-100 pt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Need help with billing?</h3>
            <p className="text-sm text-gray-500">
              If you have questions about your subscription or need help with billing, please 
              contact our support team at <a href="mailto:support@up247.org" className="text-blue-600 hover:underline">support@up247.org</a>.
            </p>
          </CardFooter>
        </Card>
        
        {/* Billing History Link */}
        <div className="text-center">
          <Button 
            variant="outline" 
            onClick={handleViewBillingHistory}
            disabled={openingBillingPortal}
          >
            {openingBillingPortal ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Opening Billing Portal...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" /> View Billing History
              </>
            )}
          </Button>
          <p className="text-xs text-gray-500 mt-2">
            You'll be redirected to our secure payment provider to view your billing history.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MySubscription;
